import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import SearchBar from './components/SearchBar';
import FilterChips from './components/FilterChips';
import FilterSidebar from './components/FilterSidebar';
import SortDropdown from './components/SortDropdown';
import CourseGrid from './components/CourseGrid';
import FilterModal from './components/FilterModal';
import Button from '../../components/ui/Button';
import { listCatalogListings } from '../../utils/catalogHelpers';

// Mock fallback — shown only when the live catalog is empty.
// TODO: remove once there are enough real published courses on the platform.
const mockCourses = [
  {
    id: 'mock-1',
    title: "Complete Machine Learning Bootcamp",
    description: "Master machine learning from basics to advanced concepts with hands-on projects and real-world applications.",
    category: "Artificial Intelligence",
    price: 199.99,
    isFree: false,
    providerName: "Nesolearn Academy",
    providerVerified: true,
    enrollmentType: "open",
    moduleCount: 8,
    lessonCount: 42,
    thumbnail: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=225&fit=crop",
    isLive: false,
  },
  {
    id: 'mock-2',
    title: "AI Ethics and Responsible Development",
    description: "Learn about ethical considerations in AI development, bias mitigation, and responsible AI practices.",
    category: "Artificial Intelligence",
    price: 0,
    isFree: true,
    providerName: "Nesolearn Academy",
    providerVerified: true,
    enrollmentType: "open",
    moduleCount: 4,
    lessonCount: 16,
    thumbnail: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?w=400&h=225&fit=crop",
    isLive: false,
  },
  {
    id: 'mock-3',
    title: "Blockchain Compliance Fundamentals",
    description: "Understand regulatory frameworks, AML compliance, and transaction monitoring in blockchain ecosystems.",
    category: "Blockchain & Web3",
    price: 149.99,
    isFree: false,
    providerName: "Africa Web3 Institute",
    providerVerified: true,
    enrollmentType: "cohort",
    moduleCount: 6,
    lessonCount: 24,
    thumbnail: "https://images.pixabay.com/photo/2018/05/08/08/44/artificial-intelligence-3382507_1280.jpg?w=400&h=225&fit=crop",
    isLive: false,
  },
  {
    id: 'mock-4',
    title: "Data Science for AI Applications",
    description: "Learn data preprocessing, analysis, and visualization techniques essential for AI projects.",
    category: "Data Science",
    price: 99.99,
    isFree: false,
    providerName: "Nesolearn Academy",
    providerVerified: true,
    enrollmentType: "open",
    moduleCount: 5,
    lessonCount: 20,
    thumbnail: "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?w=400&h=225&fit=crop",
    isLive: false,
  },
];

const normalizeLive = (listing) => ({
  id: listing?.courseId,
  courseId: listing?.courseId,
  orgId: listing?.providerId,
  title: listing?.title,
  description: listing?.description,
  category: listing?.category || "Other",
  price: listing?.priceNesol || 0,
  isFree: listing?.isFree !== false,
  providerName: listing?.providerName || "Unknown Provider",
  providerVerified: !!listing?.providerVerified,
  enrollmentType: listing?.enrollmentType || "invite",
  moduleCount: listing?.moduleCount || 0,
  lessonCount: listing?.lessonCount || 0,
  thumbnail: listing?.thumbnailUrl || "",
  isLive: true,
});

const CourseCatalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [activeFilters, setActiveFilters] = useState({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [liveCourses, setLiveCourses] = useState([]);

  useEffect(() => {
    const loadLive = async () => {
      setIsLoading(true);
      const { listings } = await listCatalogListings();
      setLiveCourses((listings || [])?.map(normalizeLive));
      setIsLoading(false);
    };
    loadLive();
  }, []);

  const usingLiveData = liveCourses?.length > 0;
  const sourceCourses = usingLiveData ? liveCourses : mockCourses;

  // Category filter options are built dynamically from whatever's actually in the dataset
  const categoryOptions = useMemo(() => {
    const counts = {};
    sourceCourses?.forEach((c) => {
      counts[c?.category] = (counts?.[c?.category] || 0) + 1;
    });
    return Object.entries(counts)?.map(([category, count]) => ({
      value: category?.toLowerCase()?.replace(/\s+/g, '-'),
      label: category,
      count,
    }));
  }, [sourceCourses]);

  const filterConfig = [
    {
      key: 'category',
      label: 'Category',
      icon: 'BookOpen',
      options: categoryOptions,
    },
    {
      key: 'price',
      label: 'Price',
      icon: 'DollarSign',
      options: [
        { value: 'free', label: 'Free', count: sourceCourses?.filter((c) => c?.isFree)?.length },
        { value: 'paid', label: 'Paid', count: sourceCourses?.filter((c) => !c?.isFree)?.length },
      ],
    },
    {
      key: 'enrollment',
      label: 'Enrollment',
      icon: 'Users',
      options: [
        { value: 'open', label: 'Open Enrollment', count: sourceCourses?.filter((c) => c?.enrollmentType === 'open')?.length },
        { value: 'invite', label: 'Invite Only', count: sourceCourses?.filter((c) => c?.enrollmentType === 'invite')?.length },
        { value: 'cohort', label: 'Cohort', count: sourceCourses?.filter((c) => c?.enrollmentType === 'cohort')?.length },
      ],
    },
  ];

    const filteredAndSortedCourses = useMemo(() => {
    let filtered = [...sourceCourses];

    if (categoryParam) {
      filtered = filtered?.filter((c) => c?.category?.toLowerCase() === categoryParam?.toLowerCase());
    }

    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase();
      filtered = filtered?.filter(
        (c) =>
          c?.title?.toLowerCase()?.includes(q) ||
          c?.description?.toLowerCase()?.includes(q) ||
          c?.category?.toLowerCase()?.includes(q) ||
          c?.providerName?.toLowerCase()?.includes(q)
      );
    }

    const categoryFilters = Object.keys(activeFilters)?.filter((k) => k?.startsWith('category_') && activeFilters?.[k]);
    if (categoryFilters?.length > 0) {
      const cats = categoryFilters?.map((k) => k?.replace('category_', ''));
      filtered = filtered?.filter((c) => cats?.includes(c?.category?.toLowerCase()?.replace(/\s+/g, '-')));
    }

    if (activeFilters?.price_free) filtered = filtered?.filter((c) => c?.isFree);
    if (activeFilters?.price_paid) filtered = filtered?.filter((c) => !c?.isFree);

    if (activeFilters?.enrollment_open) filtered = filtered?.filter((c) => c?.enrollmentType === 'open');
    if (activeFilters?.enrollment_invite) filtered = filtered?.filter((c) => c?.enrollmentType === 'invite');
    if (activeFilters?.enrollment_cohort) filtered = filtered?.filter((c) => c?.enrollmentType === 'cohort');

    switch (sortBy) {
      case 'title_asc':
        filtered?.sort((a, b) => a?.title?.localeCompare(b?.title));
        break;
      case 'price_asc':
        filtered?.sort((a, b) => a?.price - b?.price);
        break;
      case 'price_desc':
        filtered?.sort((a, b) => b?.price - a?.price);
        break;
      case 'newest':
      default:
        break;
    }

    return filtered;
    }, [sourceCourses, searchQuery, activeFilters, sortBy, categoryParam]);

  const handleFilterChange = (key, value) => setActiveFilters((prev) => ({ ...prev, [key]: value }));
  const handleClearFilters = () => setActiveFilters({});

  return (
    <>
      <Helmet>
        <title>Course Catalog - Nesolearn Platform</title>
        <meta name="description" content="Browse verified courses from institutions and educators across the Nesolearn ecosystem." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <NavigationBar />

          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 sm:pt-32 pb-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Course Catalog</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover verified courses from institutions across the Nesolearn ecosystem
            </p>
                        {!usingLiveData && !isLoading && (
              <p className="text-xs text-warning mt-3">
                Showing sample courses, live courses from verified institutions will appear here as they publish.
              </p>
            )}
            {categoryParam && (
              <div className="flex justify-center mt-4">
                <span className="inline-flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-3.5 py-1.5 rounded-full">
                  {categoryParam}
                  <button
                    type="button"
                    onClick={() => setSearchParams({})}
                    className="hover:text-gray-300 transition-colors"
                    aria-label="Clear category filter"
                  >
                    ✕
                  </button>
                </span>
              </div>
            )}
          </div>

          <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} onSearchSubmit={() => {}} />

          <div className="lg:hidden">
            <FilterChips
              filters={filterConfig}
              activeFilters={activeFilters}
              onFilterChange={handleFilterChange}
              onShowMoreFilters={() => setIsFilterModalOpen(true)}
            />
          </div>

          <div className="flex gap-8">
            <div className="hidden lg:block flex-shrink-0">
              <FilterSidebar
                filters={filterConfig}
                activeFilters={activeFilters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />
            </div>

            <div className="flex-1">
              <SortDropdown sortBy={sortBy} onSortChange={setSortBy} resultsCount={filteredAndSortedCourses?.length} />
              <CourseGrid courses={filteredAndSortedCourses} isLoading={isLoading} />
            </div>
          </div>
        </main>

        <FilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          filters={filterConfig}
          activeFilters={activeFilters}
          onFilterChange={handleFilterChange}
          onApplyFilters={() => setIsFilterModalOpen(false)}
          onClearFilters={handleClearFilters}
        />
      </div>
    </>
  );
};

export default CourseCatalog;